import { useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { deleteFile, uploadFile } from "api";
import { CreateExtractedPackingListJobRequestPayload } from "api/request/documentProcessor.request";
import { createExtractedPackingListJob } from "api/services/documentProcessor";
import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down.svg";
import Button from "components/v2/Button/Button";
import InputDropdown from "components/v2/InputDropdown/InputDropdown";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { FormattedUploadedFile } from "types/Common";
import UploadDropzone from "../UploadDropzone";

interface DocumentInputProps {
  refetchExtractedPackingListTable: () => void;
}

export default function DocumentInput({ refetchExtractedPackingListTable }: DocumentInputProps) {
  const intl = useIntl();

  const [searchParams] = useSearchParams();
  const activeKey = searchParams.get("request_type") || "quickrequest";

  const { permissions } = usePermissions();
  const { payment_location } = JSON.parse(permissions || "{}");
  const paymentLocationOptions =
    payment_location?.locations.map((location: any) => ({
      ...location,
      label: location.label,
      value: {
        label: location.label,
        value: location.value,
      },
    })) || [];

  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [paymentLocation, setPaymentLocation] = useState<{
    label: string;
    value: string;
  }>();
  const [file, setFile] = useState<FormattedUploadedFile | null>(null);

  const { mutate: createExtractedPackingListJobMutation, isLoading: isCreating } = useMutation({
    mutationFn: (payload: CreateExtractedPackingListJobRequestPayload) => createExtractedPackingListJob(payload),
    onSuccess: () => {
      setFile(null);
      refetchExtractedPackingListTable();
    },
    onError: (err) => errorToast(err),
  });

  const disableUploadDropzone = !paymentLocation;
  const disableConfirmButton = !paymentLocation || !file || isCreating || isUploading || isDeleting;

  const handleClickSubmit = () => {
    if (!paymentLocation || !file || isCreating) return;
    createExtractedPackingListJobMutation({
      payment_location_reference_number: paymentLocation.value,
      file_reference_number: file.reference,
      request_type: activeKey,
    });
  };

  const handleFileSelected = (files: File[]) => {
    const file = files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);
    setIsUploading(true);
    setFile({
      fileName: file.name,
      size: file.size,
      file_id: 0,
      reference: "",
      url: "",
    });

    uploadFile(formData)
      .then((res) => {
        if (res.status === 201) {
          const formattedFile = {
            file_id: res.data.id,
            fileName: res.data.name,
            url: res.data.url,
            reference: res.data.reference_number,
            size: file.size,
          };
          setFile(formattedFile);
        }
      })
      .catch((error) => {
        errorToast(error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleRemoveFile = () => {
    if (!file) return;
    const { reference } = file;
    setIsDeleting(true);

    deleteFile(reference)
      .then(() => {
        setFile(null);
      })
      .catch((err) => {
        errorToast(err, "Could not delete file, please try again later");
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <>
      <InputDropdown
        label={intl.formatMessage({
          id: "general.paymentLocation",
          defaultMessage: "Payment location",
        })}
        iconRight={<ChevronDown />}
        required
        options={paymentLocationOptions}
        value={paymentLocation?.value}
        onSelect={(value) => setPaymentLocation(value)}
        onChange={() => setPaymentLocation(undefined)}
      />

      <UploadDropzone
        disabled={disableUploadDropzone}
        onFileSelected={handleFileSelected}
        uploadedFile={file}
        onRemoveFile={handleRemoveFile}
        isUploading={isUploading}
        isDeleting={isDeleting}
      />

      <div className="d-flex justify-content-end">
        <Button
          label={intl.formatMessage({
            id: "general.submit",
            defaultMessage: "Submit",
          })}
          onClick={handleClickSubmit}
          disabled={disableConfirmButton}
          size="sm"
        />
      </div>
    </>
  );
}
